import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getPulseAboutCopyrightYear, PULSE_ABOUT } from '../config/pulseAbout';
import { PULSE_ABOUT_LEGAL } from '../config/pulseAboutLegal';
import '../styles/pulse-about-premium.css';

function LegalSubsection({ subsection }) {
  return (
    <div className="pulse-about-legal__sub">
      {subsection.title ? (
        <h4 className="pulse-about-legal__subtitle">{subsection.title}</h4>
      ) : null}
      {subsection.paragraphs?.map((p, i) => (
        <p key={`p-${i}`} className="pulse-about-legal__p">
          {p}
        </p>
      ))}
      {subsection.bullets?.length ? (
        <ul className="pulse-about-legal__list">
          {subsection.bullets.map((item, i) => (
            <li key={`b-${i}`}>{item}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LegalSection({ section }) {
  return (
    <section className="pulse-about-legal__section" id={`pulse-about-sec-${section.num}`}>
      <h3 className="pulse-about-legal__heading">
        <span className="pulse-about-legal__num">{section.num}.</span> {section.title}
      </h3>
      {section.paragraphs?.map((p, i) => (
        <p key={`p-${i}`} className="pulse-about-legal__p">
          {p}
        </p>
      ))}
      {section.bullets?.length ? (
        <ul className="pulse-about-legal__list">
          {section.bullets.map((item, i) => (
            <li key={`b-${i}`}>{item}</li>
          ))}
        </ul>
      ) : null}
      {section.subsections?.map((sub, i) => (
        <LegalSubsection key={`sub-${i}`} subsection={sub} />
      ))}
    </section>
  );
}

function AboutPanelContent({ onClose }) {
  const year = getPulseAboutCopyrightYear();
  const legal = PULSE_ABOUT_LEGAL;

  return (
    <div
      className="pulse-about-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pulse-about-title"
    >
      <div className="pulse-about-panel__accent" aria-hidden="true" />

      <header className="pulse-about-panel__head">
        <div className="pulse-about-panel__brand">
          <span className="pulse-about-panel__mark" aria-hidden="true">
            ◈
          </span>
          <div>
            <p className="pulse-about-panel__eyebrow">
              {PULSE_ABOUT.productName} · {legal.brandLine}
            </p>
            <h2 id="pulse-about-title" className="pulse-about-panel__title">
              Propiedad de {PULSE_ABOUT.legalName}
            </h2>
            <p className="pulse-about-panel__sub">{legal.docSubtitle}</p>
          </div>
        </div>
        <button
          type="button"
          className="pulse-about-panel__close"
          onClick={onClose}
          aria-label="Cerrar información legal"
        >
          ×
        </button>
      </header>

      <div className="pulse-about-panel__body" tabIndex={0} aria-label="Términos y condiciones Pulse CRM">
        <section className="pulse-about-card pulse-about-card--hero">
          <p className="pulse-about-card__label">Titular del proyecto</p>
          <strong className="pulse-about-card__value">{PULSE_ABOUT.legalName}</strong>
          <p className="pulse-about-card__hint">
            Todo Grupo Bienestar CRM es desarrollo y propiedad de {PULSE_ABOUT.legalName} · {PULSE_ABOUT.groupName}
          </p>
          <dl className="pulse-about-meta">
            <div className="pulse-about-meta__row">
              <dt>Domicilio</dt>
              <dd>{legal.address}</dd>
            </div>
            <div className="pulse-about-meta__row">
              <dt>Ámbito</dt>
              <dd>{legal.territory}</dd>
            </div>
            <div className="pulse-about-meta__row">
              <dt>Versión</dt>
              <dd>{legal.docVersion}</dd>
            </div>
          </dl>
        </section>

        <section className="pulse-about-card pulse-about-card--ownership">
          <p className="pulse-about-card__label">Propiedad del software</p>
          {legal.ownership.map((p, i) => (
            <p key={i} className="pulse-about-card__text">
              {p}
            </p>
          ))}
        </section>

        <aside className="pulse-about-notice" role="note">
          {legal.notice}
        </aside>

        <div className="pulse-about-legal">
          {legal.sections.map((section) => (
            <LegalSection key={section.num} section={section} />
          ))}
        </div>
      </div>

      <footer className="pulse-about-panel__foot">
        <p className="pulse-about-panel__copyright">
          © {year} {PULSE_ABOUT.legalName} · Todos los derechos reservados.
        </p>
        <p className="pulse-about-panel__meta">
          Pulse CRM v{PULSE_ABOUT.version}
          <span aria-hidden="true"> · </span>
          {legal.docVersion}
          <span aria-hidden="true"> · </span>
          Uso exclusivo operadores {PULSE_ABOUT.groupName}
        </p>
      </footer>
    </div>
  );
}

/** Dock lateral (peek + panel), debajo de Pulse Command */
export function PulseAboutDock({ isOpen, onOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={`pulse-about-shell${isOpen ? ' is-expanded' : ''}`} aria-live="polite">
      {isOpen ? (
        <>
          <button
            type="button"
            className="pulse-about-shell__backdrop"
            onClick={onClose}
            aria-label="Cerrar información legal"
          />
          <AboutPanelContent onClose={onClose} />
        </>
      ) : (
        <div className="pulse-about-shell__rail">
          <button
            type="button"
            className="pulse-about-shell__btn"
            onClick={onOpen}
            aria-label="Términos y copyright Pulse CRM · SANTA ANA S.R.L."
            title="Términos Pulse CRM · SANTA ANA S.R.L."
          >
            <span className="pulse-about-shell__btn-icon" aria-hidden="true">
              ©
            </span>
            <span className="pulse-about-shell__btn-key" aria-hidden="true">
              i
            </span>
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}

export default PulseAboutDock;
